import { createSlice, current, type PayloadAction } from '@reduxjs/toolkit';
import { isEqual, memoize } from 'lodash-es';

import type { SortOption } from '../../utils/sort/types';
import type {
  SerializableEndpointError,
  SerializableError,
} from '../serializable_error';
import { DEFAULT_PER_PAGE } from './constants';
import type { CommonListAppState, PaginationResponse } from './types';

const initialPaginationState = {
  page: 1,
  pages: 1,
  total: 0,
  per_page: 0,
};

export const initialCommonListState: CommonListAppState = {
  ids: [],
  page: 1,
  perPage: DEFAULT_PER_PAGE,
  pagination: initialPaginationState,
  sort: [
    {
      name: 'name',
      order: 'asc',
    },
  ],
  isLoading: false,
  status: 'pending',
  fetchStatus: 'idle',
};

export const getCommonListSlice = memoize(
  <ListName extends string>(
    listName: ListName,
    customInitialState?: CommonListAppState,
  ) => {
    // Set of ids to quickly check if id is already in the list.
    // Makes sense where amount of updates can become enormous, and
    // it's vital to be performant in checking uniqueness of ids there
    // to reduce unnecessary state updates and component renders.
    let listIds = new Set<string>();

    const ensureIdExists = (id: string): boolean => {
      if (listIds.has(id)) {
        return true;
      }
      listIds.add(id);
      return false;
    };

    const initialState = customInitialState ?? initialCommonListState;

    return createSlice({
      name: listName,
      initialState,
      reducers: {
        setIds: (state, action: PayloadAction<string[]>) => {
          listIds = new Set(action.payload);
          state.ids = action.payload;
        },

        deleteId: (state, action: PayloadAction<string>) => {
          listIds.delete(action.payload);
          state.ids = state.ids.filter((id) => id !== action.payload);

          state.pagination.total -= 1;
          if (state.pagination.total < 0) {
            state.pagination.total = 0;
          }
        },

        deleteIds: (state, action: PayloadAction<string[]>) => {
          const idsToDelete = new Set(action.payload);
          action.payload.forEach((id) => {
            listIds.delete(id);
          });
          state.ids = state.ids.filter((id) => !idsToDelete.has(id));

          state.pagination.total -= idsToDelete.size;
          if (state.pagination.total < 0) {
            state.pagination.total = 0;
          }
        },

        prependId: (state, action: PayloadAction<string>) => {
          if (ensureIdExists(action.payload)) {
            return state;
          }
          state.ids.unshift(action.payload);
        },

        appendId: (state, action: PayloadAction<string>) => {
          if (ensureIdExists(action.payload)) {
            return state;
          }
          state.ids.push(action.payload);
        },

        appendIds: (state, action: PayloadAction<string[]>) => {
          const idsToAppend: string[] = [];
          action.payload.forEach((id) => {
            if (!ensureIdExists(id)) {
              idsToAppend.push(id);
            }
          });
          if (idsToAppend.length === 0) {
            return state;
          }
          state.ids = state.ids.concat(idsToAppend);
        },

        setSort: (state, action: PayloadAction<SortOption[]>) => {
          const isSameSort = isEqual(current(state.sort), action.payload);
          state.sort = action.payload;
          if (!isSameSort && state.nextSearchAfter) {
            state.nextSearchAfter = undefined;
            state.page = 1;
          }
        },

        setPagination: (state, action: PayloadAction<PaginationResponse>) => {
          state.pagination = action.payload;
        },

        resetPagination: (state) => {
          state.pagination = { ...initialState.pagination };
          state.nextSearchAfter = initialState.nextSearchAfter;
        },

        setPage: (state, action: PayloadAction<number>) => {
          state.page = action.payload;
        },

        setPerPage: (state, action: PayloadAction<number>) => {
          state.perPage = action.payload;
        },

        /**
         * @deprecated Use `setFetchStatus('fetching')` instead
         */
        setLoading: (state, action: PayloadAction<boolean>) => {
          state.isLoading = action.payload;
          state.fetchStatus = action.payload ? 'fetching' : 'idle';
        },

        setStatus: (
          state,
          action: PayloadAction<CommonListAppState['status']>,
        ) => {
          state.status = action.payload;
        },

        setFetchStatus: (
          state,
          action: PayloadAction<CommonListAppState['fetchStatus']>,
        ) => {
          state.isLoading = action.payload === 'fetching';
          state.fetchStatus = action.payload;
        },

        setError: (
          state,
          action: PayloadAction<SerializableEndpointError | SerializableError>,
        ) => {
          state.error = action.payload;
        },

        clearError: (state) => {
          state.error = undefined;
        },

        setNextSearchAfter: (
          state,
          action: PayloadAction<unknown[] | undefined>,
        ) => {
          state.nextSearchAfter = action.payload;
        },

        resetItems: (state) => {
          listIds.clear();
          state.ids = [];
          state.pagination = { ...initialState.pagination };
          state.nextSearchAfter = initialState.nextSearchAfter;
          state.status = 'pending';
          state.error = undefined;
        },

        reset: () => {
          listIds.clear();
          return initialState;
        },
      },
    });
  },
);
